/**
 * @module ol/source/ImageStatic
 */

import EventType from 'ol/events/EventType';
import ImageSource, {defaultImageLoadFunction} from 'ol/source/Image';
import ImageState from 'ol/ImageState';
import ImageWrapper from 'ol/Image';
import {createCanvasContext2D} from 'ol/dom';
import {getHeight, getWidth, intersects} from 'ol/extent';
import {get as getProjection} from 'ol/proj';

/**
 * @typedef {Object} Options
 * @property {import("./Source.js").AttributionLike} [attributions] Attributions.
 * @property {null|string} [crossOrigin] The `crossOrigin` attribute for loaded images.  Note that
 * you must provide a `crossOrigin` value if you want to access pixel data with the Canvas renderer.
 * See https://developer.mozilla.org/en-US/docs/Web/HTML/CORS_enabled_image for more detail.
 * @property {import("../extent.js").Extent} [imageExtent] Extent of the image in map coordinates.
 * This is the [left, bottom, right, top] map coordinates of your image.
 * @property {import("../Image.js").LoadFunction} [imageLoadFunction] Optional function to load an image given a URL.
 * @property {boolean} [interpolate=true] Use interpolated values when resampling.  By default,
 * linear interpolation is used when resampling.  Set to false to use the nearest neighbor instead.
 * @property {import("../proj.js").ProjectionLike} [projection] Projection. Default is the view projection.
 * @property {import("../size.js").Size} [imageSize] Size of the image in pixels. Usually the image size is auto-detected, so this
 * only needs to be set if auto-detection fails for some reason.
 * @property {string} url Image URL.
 */

/**
 * @classdesc
 * A layer source for displaying a single, static image.
 * @api
 */
class Static extends ImageSource {
  /**
   * @param {Options} options ImageStatic options.
   */
  constructor(options) {
    const crossOrigin =
      options.crossOrigin !== undefined ? options.crossOrigin : null;

    const /** @type {import("../Image.js").LoadFunction} */ imageLoadFunction =
        options.imageLoadFunction !== undefined
          ? options.imageLoadFunction
          : defaultImageLoadFunction;

    super({
      attributions: options.attributions,
      interpolate: options.interpolate,
      projection: getProjection(options.projection),
    });

    /**
     * @private
     * @type {string}
     */
    this.url_ = options.url;

    /**
     * @private
     * @type {import("../extent.js").Extent}
     */
    this.imageExtent_ = options.imageExtent;

    /**
     * @private
     * @type {import("../Image.js").default}
     */
    this.image_ = new ImageWrapper(
      this.imageExtent_,
      undefined,
      1,
      this.url_,
      crossOrigin,
      imageLoadFunction
    );

    /**
     * @private
     * @type {import("../size.js").Size|null}
     */
    this.imageSize_ = options.imageSize ? options.imageSize : null;

    this.image_.addEventListener(
      EventType.CHANGE,
      this.handleImageChange.bind(this)
    );
  }

  /**
   * Returns the image extent
   * @return {import("../extent.js").Extent} image extent.
   * @api
   */
  getImageExtent() {
    return this.imageExtent_;
  }

  /**
   * @param {import("../extent.js").Extent} extent Extent.
   * @param {number} resolution Resolution.
   * @param {number} pixelRatio Pixel ratio.
   * @param {import("../proj/Projection.js").default} projection Projection.
   * @return {import("../Image.js").default} Single image.
   */
  getImageInternal(extent, resolution, pixelRatio, projection) {
    if (intersects(extent, this.image_.getExtent())) {
      return this.image_;
    }
    return null;
  }

  /**
   * Return the URL used for this image source.
   * @return {string} URL.
   * @api
   */
  getUrl() {
    return this.url_;
  }

  /**
   * @param {import("../events/Event.js").default} evt Event.
   */
  handleImageChange(evt) {
    if (this.image_.getState() == ImageState.LOADED) {
		const imageExtent = this.image_.getExtent();
		const unrotatedExtent = this.get("unrotatedExtent");
		const image = this.get("image") || this.image_.getImage();
		
		if(!this.get("image")) {
			this.set("image", image);
		}
		
		// Width and height of actual image
		const imageWidth = image.width;
		const imageHeight = image.height;
		
		// Extent width and height (actual extent with rotation factored in)
		const extentWidth = getWidth(imageExtent);
		const extentHeight = getHeight(imageExtent);
		
		// Unrotated extent width and height (no rotation factored in- remains constant)
		const unrotatedExtentWidth = unrotatedExtent ? getWidth(unrotatedExtent) : extentWidth;
		const unrotatedExtentHeight = unrotatedExtent ? getHeight(unrotatedExtent) : extentHeight;
		
		// x and y resolutions
		const xResolution = imageWidth / extentWidth;
		const yResolution = imageHeight / extentHeight;
		
		// Multiplier for canvas resolution, either x or y, whichever is bigger
		const mult = xResolution > yResolution ? xResolution : yResolution;
		
		// Canvas width and height
		const canvasWidth = extentWidth * mult;
		const canvasHeight = extentHeight * mult;
		
		// Drawn image width and height
		const drawImageWidth = unrotatedExtentWidth * mult;
		const drawImageHeight = unrotatedExtentHeight * mult;
		
    	const context = createCanvasContext2D(canvasWidth, canvasHeight);
    	const canvas = context.canvas;
		
		const rotation = this.get("rotation") || 0;
		
		context.imageSmoothingEnabled = this.getInterpolate();
		
		context.translate(canvasWidth / 2, canvasHeight / 2);
		context.rotate(-rotation);
		
    	context.drawImage(
			image,
			-(drawImageWidth / 2), 
			-(drawImageHeight / 2),
			drawImageWidth,
			drawImageHeight
    	);
		
    	this.image_.setImage(canvas);
    }
    super.handleImageChange(evt);
  }
}

export default Static;
